'use client';

import _objectWithoutProperties from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
var _excluded = ["className"],
    _excluded2 = ["alignItems", "children", "className", "component", "components", "componentsProps", "ContainerComponent", "ContainerProps", "dense", "disableGutters", "disablePadding", "divider", "secondaryAction", "slotProps", "slots"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import * as React from 'react';
import clsx from 'clsx';
import composeClasses from '@mui/utils/composeClasses';
import elementTypeAcceptingRef from '@mui/utils/elementTypeAcceptingRef';
import chainPropTypes from '@mui/utils/chainPropTypes';
import isHostComponent from "../utils/isHostComponent.js";
import { styled } from "../zero-styled/index.js";
import memoTheme from "../utils/memoTheme.js";
import { useDefaultProps } from "../DefaultPropsProvider/index.js";
import isMuiElement from "../utils/isMuiElement.js";
import useForkRef from "../utils/useForkRef.js";
import ListContext from "../List/ListContext.js";
import { getListItemUtilityClass } from "./listItemClasses.js";
import { listItemButtonClasses } from "../ListItemButton/index.js";
import ListItemSecondaryAction from "../ListItemSecondaryAction/index.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export var overridesResolver = function overridesResolver(props, styles) {
  var ownerState = props.ownerState;
  return [styles.root, ownerState.dense && styles.dense, ownerState.alignItems === 'flex-start' && styles.alignItemsFlexStart, ownerState.divider && styles.divider, !ownerState.disableGutters && styles.gutters, !ownerState.disablePadding && styles.padding, ownerState.hasSecondaryAction && styles.secondaryAction];
};

var useUtilityClasses = function useUtilityClasses(ownerState) {
  var alignItems = ownerState.alignItems,
      classes = ownerState.classes,
      dense = ownerState.dense,
      disableGutters = ownerState.disableGutters,
      disablePadding = ownerState.disablePadding,
      divider = ownerState.divider,
      hasSecondaryAction = ownerState.hasSecondaryAction;
  var slots = {
    root: ['root', dense && 'dense', !disableGutters && 'gutters', !disablePadding && 'padding', divider && 'divider', alignItems === 'flex-start' && 'alignItemsFlexStart', hasSecondaryAction && 'secondaryAction'],
    container: ['container']
  };
  return composeClasses(slots, getListItemUtilityClass, classes);
};

export var ListItemRoot = styled('div', {
  name: 'MuiListItem',
  slot: 'Root',
  overridesResolver: overridesResolver
})(memoTheme(function (_ref) {
  var theme = _ref.theme;
  return {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    position: 'relative',
    textDecoration: 'none',
    width: '100%',
    boxSizing: 'border-box',
    textAlign: 'left',
    variants: [{
      props: function props(_ref2) {
        var ownerState = _ref2.ownerState;
        return !ownerState.disablePadding;
      },
      style: {
        paddingTop: 8,
        paddingBottom: 8
      }
    }, {
      props: function props(_ref3) {
        var ownerState = _ref3.ownerState;
        return !ownerState.disablePadding && ownerState.dense;
      },
      style: {
        paddingTop: 4,
        paddingBottom: 4
      }
    }, {
      props: function props(_ref4) {
        var ownerState = _ref4.ownerState;
        return !ownerState.disablePadding && !ownerState.disableGutters;
      },
      style: {
        paddingLeft: 16,
        paddingRight: 16
      }
    }, {
      props: function props(_ref5) {
        var ownerState = _ref5.ownerState;
        return !ownerState.disablePadding && !!ownerState.secondaryAction;
      },
      style: {
        // Add some space to avoid collision as `ListItemSecondaryAction`
        // is absolutely positioned.
        paddingRight: 48
      }
    }, {
      props: function props(_ref6) {
        var ownerState = _ref6.ownerState;
        return !!ownerState.secondaryAction;
      },
      style: _defineProperty({}, "& > .".concat(listItemButtonClasses.root), {
        paddingRight: 48
      })
    }, {
      props: {
        alignItems: 'flex-start'
      },
      style: {
        alignItems: 'flex-start'
      }
    }, {
      props: function props(_ref7) {
        var ownerState = _ref7.ownerState;
        return ownerState.divider;
      },
      style: {
        borderBottom: "1px solid ".concat((theme.vars || theme).palette.divider),
        backgroundClip: 'padding-box'
      }
    }, {
      props: function props(_ref8) {
        var ownerState = _ref8.ownerState;
        return ownerState.button;
      },
      style: {
        transition: theme.transitions.create('background-color', {
          duration: theme.transitions.duration.shortest
        }),
        '&:hover': {
          textDecoration: 'none',
          backgroundColor: (theme.vars || theme).palette.action.hover,
          // Reset on touch devices, it doesn't add specificity
          '@media (hover: none)': {
            backgroundColor: 'transparent'
          }
        }
      }
    }, {
      props: function props(_ref9) {
        var ownerState = _ref9.ownerState;
        return ownerState.hasSecondaryAction;
      },
      style: {
        // Add some space to avoid collision as `ListItemSecondaryAction`
        // is absolutely positioned.
        paddingRight: 48
      }
    }]
  };
}));
var ListItemContainer = styled('li', {
  name: 'MuiListItem',
  slot: 'Container',
  overridesResolver: function overridesResolver(props, styles) {
    return styles.container;
  }
})({
  position: 'relative'
});
/**
 * Uses an additional container component if `ListItemSecondaryAction` is the last child.
 */

var ListItem = /*#__PURE__*/React.forwardRef(function ListItem(inProps, ref) {
  var props = useDefaultProps({
    props: inProps,
    name: 'MuiListItem'
  });
  var _props$alignItems = props.alignItems,
      alignItems = _props$alignItems === void 0 ? 'center' : _props$alignItems,
      childrenProp = props.children,
      className = props.className,
      componentProp = props.component,
      _props$components = props.components,
      components = _props$components === void 0 ? {} : _props$components,
      _props$componentsProp = props.componentsProps,
      componentsProps = _props$componentsProp === void 0 ? {} : _props$componentsProp,
      _props$ContainerCompo = props.ContainerComponent,
      ContainerComponent = _props$ContainerCompo === void 0 ? 'li' : _props$ContainerCompo,
      _props$ContainerProps = props.ContainerProps;
  _props$ContainerProps = _props$ContainerProps === void 0 ? {} : _props$ContainerProps;

  var ContainerClassName = _props$ContainerProps.className,
      ContainerProps = _objectWithoutProperties(_props$ContainerProps, _excluded),
      _props$dense = props.dense,
      dense = _props$dense === void 0 ? false : _props$dense,
      _props$disableGutters = props.disableGutters,
      disableGutters = _props$disableGutters === void 0 ? false : _props$disableGutters,
      _props$disablePadding = props.disablePadding,
      disablePadding = _props$disablePadding === void 0 ? false : _props$disablePadding,
      _props$divider = props.divider,
      divider = _props$divider === void 0 ? false : _props$divider,
      secondaryAction = props.secondaryAction,
      _props$slotProps = props.slotProps,
      slotProps = _props$slotProps === void 0 ? {} : _props$slotProps,
      _props$slots = props.slots,
      slots = _props$slots === void 0 ? {} : _props$slots,
      other = _objectWithoutProperties(props, _excluded2);

  var context = React.useContext(ListContext);
  var childContext = React.useMemo(function () {
    return {
      dense: dense || context.dense || false,
      alignItems: alignItems,
      disableGutters: disableGutters
    };
  }, [alignItems, context.dense, dense, disableGutters]);
  var listItemRef = React.useRef(null);
  var children = React.Children.toArray(childrenProp); // v4 implementation, deprecated in v6, will be removed in v7

  var hasSecondaryAction = children.length && isMuiElement(children[children.length - 1], ['ListItemSecondaryAction']);

  var ownerState = _objectSpread(_objectSpread({}, props), {}, {
    alignItems: alignItems,
    dense: childContext.dense,
    disableGutters: disableGutters,
    disablePadding: disablePadding,
    divider: divider,
    hasSecondaryAction: hasSecondaryAction
  });

  var classes = useUtilityClasses(ownerState);
  var handleRef = useForkRef(listItemRef, ref);
  var Root = slots.root || components.Root || ListItemRoot;
  var rootProps = slotProps.root || componentsProps.root || {};

  var componentProps = _objectSpread({
    className: clsx(classes.root, rootProps.className, className)
  }, other);

  var Component = componentProp || 'li'; // v4 implementation, deprecated in v6, will be removed in v7

  if (hasSecondaryAction) {
    // Use div by default.
    Component = !componentProps.component && !componentProp ? 'div' : Component; // Avoid nesting of li > li.

    if (ContainerComponent === 'li') {
      if (Component === 'li') {
        Component = 'div';
      } else if (componentProps.component === 'li') {
        componentProps.component = 'div';
      }
    }

    return /*#__PURE__*/_jsx(ListContext.Provider, {
      value: childContext,
      children: /*#__PURE__*/_jsxs(ListItemContainer, _objectSpread(_objectSpread({
        as: ContainerComponent,
        className: clsx(classes.container, ContainerClassName),
        ref: handleRef,
        ownerState: ownerState
      }, ContainerProps), {}, {
        children: [/*#__PURE__*/_jsx(Root, _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, rootProps), !isHostComponent(Root) && {
          as: Component,
          ownerState: _objectSpread(_objectSpread({}, ownerState), rootProps.ownerState)
        }), componentProps), {}, {
          children: children
        })), children.pop()]
      }))
    });
  }

  return /*#__PURE__*/_jsx(ListContext.Provider, {
    value: childContext,
    children: /*#__PURE__*/_jsxs(Root, _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, rootProps), {}, {
      as: Component,
      ref: handleRef
    }, !isHostComponent(Root) && {
      ownerState: _objectSpread(_objectSpread({}, ownerState), rootProps.ownerState)
    }), componentProps), {}, {
      children: [children, secondaryAction && /*#__PURE__*/_jsx(ListItemSecondaryAction, {
        children: secondaryAction
      })]
    }))
  });
});
process.env.NODE_ENV !== "production" ? void 0 : void 0;
export default ListItem;